import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { Work, WorkAdd, WorkEdit, WorkSchema, WorksPaginatedSchema, SearchWorksBrowseResponse } from "@models/work";
import {
  getWorkSearchBody,
  WORK_BROWSE_INCLUDES,
  WORK_BROWSE_PER_PAGE,
  WorkSearchParams,
} from "./worksBrowse.utils";



export const worksApi = createApi({
  reducerPath: "worksApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["works", "worksBrowse", "work"],
  endpoints: (b) => ({

    searchWorksBrowse: b.query<SearchWorksBrowseResponse, WorkSearchParams>({
      query: searchWorksBrowseQuery,
      serializeQueryArgs: searchWorksBrowseSerializeQueryArgs,
      transformResponse: parseWorksBrowseResponse,
      providesTags: [{ type: "worksBrowse", id: "LIST" }],
    }),

    getWorkById: b.query<Work, string | number>({
      query: getWorkByIdQuery,
      transformResponse: parseWorkResponse,
      providesTags: getWorkByIdProvidesTags,
    }),

    addWork: b.mutation<Work, WorkAdd>({
      query: addWorkQuery,
      transformResponse: parseWorkResponse,
      invalidatesTags: addWorkInvalidatesTags,
    }),

    saveWork: b.mutation<Work, { workId: number; workToSave: WorkEdit }>({
      query: saveWorkQuery,
      transformResponse: parseWorkResponse,
      invalidatesTags: saveWorkInvalidatesTags,
    }),

    deleteWork: b.mutation<void, number>({
      query: deleteWorkQuery,
      invalidatesTags: deleteWorkInvalidatesTags,
    }),

    getNextWorkNumber: b.query<number, void>({
      query: getNextWorkNumberQuery,
      transformResponse: getNextWorkNumberTransformResponse,
    }),

  }),
});

export const {
  useSearchWorksBrowseQuery,
  useLazySearchWorksBrowseQuery,
  useGetWorkByIdQuery,
  useAddWorkMutation,
  useSaveWorkMutation,
  useDeleteWorkMutation,
  useLazyGetNextWorkNumberQuery,
} = worksApi;










//         *******************************************
//         *             helper functions            *
//         *******************************************


/**
 * RTK Query cache tags to invalidate after work create, update, or delete.
 * @param workId number | undefined - Specific work id when invalidating a single record.
 * @returns Array - Cache tag descriptors for works list, browse list, and optional detail.
 */
function workMutationTags(workId?: number) {

  return [
    { type: "works" as const, id: "LIST" as const },
    { type: "worksBrowse" as const, id: "LIST" as const },
    ...(workId ? [{ type: "work" as const, id: workId }] : []),
  ];
}

/**
 * Validates a single work API payload with Zod.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns Work - Parsed work entity.
 */
function parseWorkResponse(response: unknown): Work {

  return WorkSchema.parse(response);
}

/**
 * Validates a paginated works browse payload and returns the data array.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns SearchWorksBrowseResponse - Parsed paginated works payload with rows and cursor meta.
 */
function parseWorksBrowseResponse(response: unknown): SearchWorksBrowseResponse {

  return WorksPaginatedSchema.parse(response);
}

/**
 * Builds a stable cache key for works browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs WorkSearchParams - Browse filters, includes, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchWorksBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: WorkSearchParams }): string {

  return `${endpointName}:${stableSerialize(queryArgs)}`;
}

/**
 * Builds the axios request for works browse search.
 * @param params WorkSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - URL, method, and POST body for works/search.
 */
function searchWorksBrowseQuery({ include, per_page, ...params }: WorkSearchParams) {

  return {
    url: "works/search",
    method: "POST" as const,
    // Apply browse defaults and normalize filter for Spatie search API
    data: getWorkSearchBody({
      include: include ?? WORK_BROWSE_INCLUDES,
      per_page: per_page ?? WORK_BROWSE_PER_PAGE,
      ...params,
    }),
  };
}

/**
 * Builds the axios request to fetch a single work by id.
 * @param workId string | number - Work primary key.
 * @returns object - URL and method for GET works/:id.
 */
function getWorkByIdQuery(workId: string | number) {

  return {
    url: `works/${workId}`,
    method: "GET" as const,
  };
}

/**
 * Resolves RTK Query cache tags for a single work detail query.
 * @param _res Work | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param workId string | number - Work primary key from query args.
 * @returns Array - Cache tag for the work detail entity.
 */
function getWorkByIdProvidesTags(_res: Work | undefined, _err: unknown, workId: string | number) {

  return [{ type: "work" as const, id: Number(workId) }];
}

/**
 * Builds the axios request to create a work.
 * @param workToAdd WorkAdd - Payload for the new work.
 * @returns object - URL, method, and POST body for works.
 */
function addWorkQuery(workToAdd: WorkAdd) {

  return {
    url: "works",
    method: "POST" as const,
    data: workToAdd,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after creating a work.
 * @returns Array - Cache tags for works list and browse list.
 */
function addWorkInvalidatesTags() {

  return workMutationTags();
}

/**
 * Builds the axios request to update a work.
 * @param arg object - Work id and PATCH payload.
 * @returns object - URL, method, and PATCH body for works/:id.
 */
function saveWorkQuery({ workId, workToSave }: { workId: number; workToSave: WorkEdit }) {

  return {
    url: `works/${workId}`,
    method: "PATCH" as const,
    data: workToSave,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after updating a work.
 * @param _res Work | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param arg object - Mutation arg containing workId.
 * @returns Array - Cache tags for lists and the updated work detail.
 */
function saveWorkInvalidatesTags(_res: Work | undefined, _err: unknown, { workId }: { workId: number; workToSave: WorkEdit }) {

  return workMutationTags(workId);
}

/**
 * Builds the axios request to delete a work.
 * @param workId number - Work primary key.
 * @returns object - URL and method for DELETE works/:id.
 */
function deleteWorkQuery(workId: number) {

  return {
    url: `works/${workId}`,
    method: "DELETE" as const,
  };
}

/**
 * Resolves RTK Query cache tags to invalidate after deleting a work.
 * @param _res void | undefined - Successful mutation result (unused).
 * @param _err unknown - Mutation error (unused).
 * @param workId number - Deleted work primary key.
 * @returns Array - Cache tags for lists and the deleted work detail.
 */
function deleteWorkInvalidatesTags(_res: void | undefined, _err: unknown, workId: number) {

  return workMutationTags(workId);
}

/**
 * Builds the axios request for the next available work number.
 * @returns object - URL and method for GET works/next-number.
 */
function getNextWorkNumberQuery() {

  return {
    url: "works/next-number",
    method: "GET" as const,
  };
}

/**
 * Maps the next-number API payload to a numeric work index.
 * @param response object - Raw response with next_work_index.
 * @returns number - Next work number from the server.
 */
function getNextWorkNumberTransformResponse(response: { next_work_index: number }): number {

  return response.next_work_index;
}